import { useState } from 'react';

import { formatCivilDate, localToCivilUtc } from './civil-date';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Calendar } from '@/new/components/shadcn/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';

interface CivilDatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  clearable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Month to open on when there's no `value` yet (e.g. an empty end date
   * opening near the chosen start date). Ignored once `value` is set.
   */
  defaultMonth?: Date | null;
}

/**
 * Date picker that produces and consumes UTC-midnight civil-date `Date`s.
 * Pairs with {@link localToCivilUtc} / {@link formatCivilDate} so the picker
 * never shifts the user's selection by a day in non-UTC timezones.
 */
export function CivilDatePicker({
  value,
  onChange,
  clearable = false,
  placeholder = 'Pick a date',
  disabled = false,
  defaultMonth = null,
}: CivilDatePickerProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" type="button" className="w-full justify-start font-normal" disabled={disabled}>
          <Icon className="lucide--calendar" />
          {value !== null ? formatCivilDate(value, 'd MMM yyyy') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" avoidCollisions={false} className="w-auto bg-white p-1">
        <Calendar
          mode="single"
          selected={value ?? undefined}
          // Open on the selected date's month, or the supplied fallback, so the
          // picker doesn't reset to today every time it opens.
          defaultMonth={value ?? defaultMonth ?? undefined}
          onSelect={(d) => {
            onChange(localToCivilUtc(d ?? null));
            setOpen(false);
          }}
          buttonVariant="default"
        />
        {clearable && value !== null && (
          <div className="flex justify-end border-t border-gray-100 p-1">
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
